import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../../services/api';
import { runRecommendationAction } from '../../utils/recommendationActions';
import Icon from '../ui/Icon';
import LearningTipsSheet from './LearningTipsSheet';
import './RecommendationCard.css';

// Presentation-only mapping from a backend-decided `type` to an icon/eyebrow
// label/emotional tone. This does NOT decide which recommendation to show,
// why, or in what order — that is entirely the backend's recommendationEngine.
// Renaming or re-skinning a type here can never change what gets recommended.
//
// `tone` buckets give each type a different emotional register (used for the
// hero card's gradient and the compact card's icon-badge color) so "worth
// revisiting" doesn't look identical to "level up" — without inventing a
// distinct layout per type, which would tip into over-designing:
//   urgent  — something needs correcting (reinforcement, targeted practice, mistakes)
//   growth  — momentum/mastery (advanced/timed challenge)
//   insight — observation, not a corrective action (behavioral, diagnostic)
const TYPE_META = {
  targeted_practice:   { icon: 'bolt',     eyebrow: 'Your Next ZAP',        cta: 'Start Practice', tone: 'urgent' },
  reinforcement:       { icon: 'refresh',  eyebrow: 'Worth Revisiting',     cta: 'Start Practice', tone: 'urgent' },
  mistake_review:      { icon: 'mistake',  eyebrow: 'Review Your Mistakes', cta: 'Review Now',     tone: 'urgent' },
  timed_challenge:     { icon: 'timer',    eyebrow: 'Speed Challenge',      cta: 'Take Challenge', tone: 'growth' },
  advanced_challenge:  { icon: 'level',    eyebrow: 'Level Up',             cta: 'Take Challenge', tone: 'growth' },
  behavioral_focus:    { icon: 'tip',      eyebrow: 'Insight',              cta: null,             tone: 'insight' },
  diagnostic_assessment: { icon: 'readiness', eyebrow: 'Get Diagnosed',    cta: 'Take Test',      tone: 'insight' },
};

const CONFIDENCE_LABEL = { high: 'High confidence', moderate: 'Moderate confidence', low: 'Low confidence', insufficient: 'Limited evidence' };

function targetLabel(target) {
  if (!target) return '';
  if (target.kind === 'topic') return target.topic_name;
  if (target.kind === 'section') return `${target.category.charAt(0).toUpperCase()}${target.category.slice(1)} section`;
  if (target.kind === 'multi_topic') return 'Multiple topics';
  if (target.kind === 'behavioral') return 'Behavioural signal';
  if (target.kind === 'test') return target.test_name;
  return 'General practice';
}

// variant: 'hero' (dashboard, one large card) | 'compact' (list row, diagnostics page)
// readOnly: true when an admin is viewing another student's recommendations —
// suppresses the action button (starting practice/tests as the student makes
// no sense from an admin drill-down), everything else renders identically.
const RecommendationCard = ({ rec, variant = 'compact', readOnly = false }) => {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [error, setError] = useState('');
  const [showTips, setShowTips] = useState(false);

  const meta = TYPE_META[rec.type] || { icon: 'tip', eyebrow: 'Suggestion', cta: rec.action ? 'View' : null, tone: 'insight' };
  const hasAction = !!rec.action; // never render a button for action: null — nothing to fabricate
  // Set only when the recommendation engine found authored Learning Tips for the topic it's already targeting
  // (audit Part 17) — this button never appears for a recommendation the engine wasn't already going to make.
  const hasTips = !!rec.learning_content_topic_id;

  const handleStart = async () => {
    setBusy(true);
    setError('');
    try {
      await runRecommendationAction(rec.action, { API, navigate });
    } catch (err) {
      setError(err.response?.data?.message || 'Could not start this right now. Try again.');
      setBusy(false);
    }
  };

  return (
    <div className={`rec-card rec-card--${variant} rec-card--${rec.type} rec-card--tone-${meta.tone}`}>
      <div className="rec-card-top">
        <span className={`rec-card-icon rec-card-icon--${meta.tone}`}><Icon name={meta.icon} size={variant === 'hero' ? 24 : 20} /></span>
        <div className="rec-card-headings">
          <span className="rec-card-eyebrow">{meta.eyebrow}</span>
          <h4 className="rec-card-target">{targetLabel(rec.target)}</h4>
        </div>
        {rec.confidence && (
          <span className={`rec-card-confidence rec-conf--${rec.confidence}`}>{CONFIDENCE_LABEL[rec.confidence] || rec.confidence}</span>
        )}
      </div>

      <p className="rec-card-reason">{rec.reason}</p>

      {rec.action?.estimated_minutes && (
        <div className="rec-card-meta">
          {rec.action.params?.question_count ? `${rec.action.params.question_count} questions · ` : ''}~{rec.action.estimated_minutes} min
        </div>
      )}

      {rec.evidence && (
        <button type="button" className="rec-card-evidence-toggle" onClick={() => setExpanded(v => !v)}>
          {expanded ? 'Hide evidence' : 'Why am I seeing this?'}
        </button>
      )}
      {expanded && rec.evidence && (
        <div className="rec-card-evidence">
          {rec.evidence.accuracy_pct !== undefined && <div>Accuracy: <b>{rec.evidence.accuracy_pct}%</b> across {rec.evidence.attempts} questions</div>}
          {rec.evidence.trend?.label && rec.evidence.trend.label !== 'insufficient_data' && (
            <div>Trend: <b>{rec.evidence.trend.label}</b>{rec.evidence.trend.improvement_delta_pct != null ? ` (${rec.evidence.trend.improvement_delta_pct > 0 ? '+' : ''}${rec.evidence.trend.improvement_delta_pct} pts)` : ''}</div>
          )}
          {rec.evidence.mistakes?.repeated_unresolved > 0 && <div>Recurring mistakes: <b>{rec.evidence.mistakes.repeated_unresolved}</b></div>}
          {rec.evidence.total_repeated_unresolved !== undefined && <div>Recurring mistakes: <b>{rec.evidence.total_repeated_unresolved}</b> across {rec.evidence.topics?.length || 0} topics</div>}
          {rec.evidence.score != null && rec.evidence.metric && <div>{rec.evidence.metric.toUpperCase()} score: <b>{Math.round(rec.evidence.score)}</b></div>}
          {rec.evidence.avg_accuracy_pct !== undefined && <div>Section average: <b>{rec.evidence.avg_accuracy_pct}%</b> vs {rec.evidence.others_avg_accuracy_pct}% elsewhere</div>}
        </div>
      )}

      {error && <div className="rec-card-error">{error}</div>}

      <div className="rec-card-actions">
        {!readOnly && hasTips && (
          <button type="button" className="rec-card-cta rec-card-cta--secondary" onClick={() => setShowTips(true)}>
            <Icon name="tip" size={14} /> Learn the technique
          </button>
        )}
        {!readOnly && hasAction && meta.cta && (
          <button type="button" className="rec-card-cta" onClick={handleStart} disabled={busy}>
            {busy ? 'Starting…' : meta.cta}
          </button>
        )}
      </div>

      {showTips && (
        <LearningTipsSheet
          topicId={rec.learning_content_topic_id}
          topicName={targetLabel(rec.target)}
          onClose={() => setShowTips(false)}
          onPractice={hasAction ? () => { setShowTips(false); handleStart(); } : undefined}
        />
      )}
    </div>
  );
};

export default RecommendationCard;
